import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { APP_VERSION } from 'src/environments/version';
import { firstValueFrom, ReplaySubject } from 'rxjs';
import { BuildConfig } from './models/build-config';
import { DeploymentConfig, Endpoint, fromBoolean } from './models/deployment-config';

@Injectable({
  providedIn: 'root',
})
export class ConfigService {
  private readonly http = inject(HttpClient);

  public readonly configLoaded = new ReplaySubject<void>();
  private deploymentConfig: DeploymentConfig | undefined;

  /**
   * Information about the build environment.
   */
  public readonly environment: BuildConfig = {
    appVersion: APP_VERSION,
  };

  async load(): Promise<void> {
    this.deploymentConfig = await firstValueFrom(this.http.get<DeploymentConfig>('/assets/config/deployment.json'));
    this.configLoaded.next();
    this.configLoaded.complete();
  }

  /** Is iproov enabled? */
  public isIproovEnabled() {
    return fromBoolean(this.deploymentConfig.iproovEnabled);
  }

  /** Is opting out of iproov allowed? */
  public isIproovOptOutAllowed() {
    return fromBoolean(this.deploymentConfig.iproovOptOutAllowed);
  }

  /** Email contact for company's System Admins */
  public getSystemAdminEmail() {
    return this.deploymentConfig.systemAdminEmail;
  }

  /** External URL for the company's FAQ */
  public getExternalFaq() {
    return this.deploymentConfig.externalFaq;
  }

  /** External URL for the company's Terms of Use */
  public getExternalTermsOfUse() {
    return this.deploymentConfig.externalTermsOfUse;
  }

  /** External URL for the company's Privacy Policy */
  public getExternalPrivacyPolicy() {
    return this.deploymentConfig.externalPrivacyPolicy;
  }

  /** How is this deployment handling phone numbers? */
  public getPhoneSetting() {
    return this.deploymentConfig.phoneEnabled;
  }

  /** Does this deployment show the additional setup pages for totp and profile pic */
  public isAdditionalSetupEnabled() {
    return fromBoolean(this.deploymentConfig.additionalSetupPages);
  }

  public isSelfRegistrationEnabled() {
    return fromBoolean(this.deploymentConfig.selfRegistrationEnabled);
  }

  /**
   * Get an endpoint, which is a URL ending with no forward slash.
   * @param endpoint The endpoint to grab
   * @returns The endpoint value
   */
  public getEndpoint(endpoint: Endpoint): string {
    return this.getDeploymentConfigEntry(endpoint);
  }

  /**
   * Get a config entry in a type-unsafe way. Throws errors if the value isn't available.
   * @param entryName The config entry to fetch
   * @returns The config entry, if it's defined
   * @throws If config isn't loaded, or the requested config entry doesn't exist
   */
  private getDeploymentConfigEntry(entryName: string): string {
    if (!this.deploymentConfig) {
      throw Error(`Config value ${entryName} was requested but config isn't loaded`);
    }
    const value = this.deploymentConfig[entryName];
    if (typeof value !== 'undefined') {
      return value;
    } else {
      throw Error(`Config value ${entryName} was requested but doesn't exist`);
    }
  }
}
